import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ChevronRight, Loader2, RefreshCw, Rows3 } from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { AdminPageHeader } from "./components/AdminUi";

const API = process.env.REACT_APP_BACKEND_URL;
const BASE_URL = `${API}/api/admin/commerciale`;
const STATES = ["Nuovo", "Contattato", "Demo programmata", "Demo effettuata", "Proposta", "Cliente", "Perso"];

function message(json, fallback) {
  return typeof json?.detail === "string" ? json.detail : fallback;
}

function stateStyle(state) {
  if (state === "Cliente") return "border-emerald-200 bg-emerald-50";
  if (state === "Perso") return "border-rose-200 bg-rose-50";
  if (state.includes("Demo")) return "border-violet-200 bg-violet-50";
  if (state === "Proposta") return "border-amber-200 bg-amber-50";
  return "border-slate-200 bg-slate-50";
}

export default function PipelineAdminModule() {
  const token = localStorage.getItem("super_admin_token");
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [movingId, setMovingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/pipeline`, { headers });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(message(json, "Impossibile caricare la pipeline"));
      setProspects(json.data || []);
    } catch (error) {
      toast.error(error.message || "Errore nel caricamento della pipeline");
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const columns = useMemo(() => STATES.map((state) => ({
    state,
    prospects: prospects.filter((prospect) => (prospect.stato_pipeline || "Nuovo") === state),
  })), [prospects]);

  const move = async (prospect, stato_pipeline) => {
    if (stato_pipeline === prospect.stato_pipeline) return;
    setMovingId(prospect.id);
    try {
      const res = await fetch(`${BASE_URL}/prospects/${prospect.id}/stato`, {
        method: "PATCH", headers, body: JSON.stringify({ stato_pipeline }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(message(json, "Spostamento non riuscito"));
      setProspects((current) => current.map((item) => item.id === prospect.id ? { ...item, stato_pipeline } : item));
      toast.success(`${prospect.nome_struttura} spostata in "${stato_pipeline}"`);
    } catch (error) {
      toast.error(error.message || "Errore durante lo spostamento");
    } finally {
      setMovingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <AdminPageHeader
        icon={Rows3}
        title="Pipeline Affidya"
        description="Lead strutture per la suite Affidya (il percorso famiglie CDRPT è in Richieste famiglie). Sposta i lead dal selettore, senza trascinamento."
        actions={<Button variant="outline" size="sm" onClick={load} disabled={loading}><RefreshCw className={`mr-1 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Aggiorna</Button>}
      />

      {loading ? <div className="py-16 text-center text-slate-500"><Loader2 className="mr-2 inline h-5 w-5 animate-spin" />Caricamento pipeline…</div>
        : <div className="flex gap-3 overflow-x-auto pb-3">
          {columns.map(({ state, prospects: columnProspects }) => (
            <section key={state} className={`w-72 shrink-0 rounded-lg border p-3 ${stateStyle(state)}`}>
              <header className="mb-3 flex items-center justify-between gap-2"><h3 className="text-sm font-semibold">{state}</h3><Badge variant="secondary">{columnProspects.length}</Badge></header>
              <div className="space-y-2">
                {columnProspects.map((prospect) => (
                  <Card key={prospect.id} className="border-slate-200 bg-white shadow-sm"><CardContent className="space-y-2 p-3">
                    <div><p className="font-medium leading-tight">{prospect.nome_struttura || "Struttura senza nome"}</p><p className="mt-1 text-xs text-slate-500">{[prospect.tipologia, prospect.comune, prospect.provincia].filter(Boolean).join(" · ") || "Località non indicata"}</p></div>
                    <div className="space-y-0.5 text-xs text-slate-600"><p>Referente: {prospect.referente || "—"}</p><p>Commerciale: {prospect.commerciale_assegnato || "Non assegnato"}</p>{prospect.prossimo_followup && <p>Follow-up: {new Date(prospect.prossimo_followup).toLocaleDateString("it-IT")}</p>}</div>
                    <Select value={prospect.stato_pipeline || "Nuovo"} onValueChange={(value) => move(prospect, value)} disabled={movingId === prospect.id}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATES.map((item) => <SelectItem key={item} value={item}>{item}</SelectItem>)}</SelectContent>
                    </Select>
                    {movingId === prospect.id && <p className="text-xs text-slate-500"><Loader2 className="mr-1 inline h-3 w-3 animate-spin" />Spostamento…</p>}
                  </CardContent></Card>
                ))}
                {!columnProspects.length && <p className="rounded border border-dashed border-slate-300 py-5 text-center text-xs text-slate-500">Nessun lead</p>}
              </div>
              {state !== STATES[STATES.length - 1] && <p className="mt-3 flex items-center text-xs text-slate-400">Avanza con il selettore <ChevronRight className="h-3 w-3" /></p>}
            </section>
          ))}
        </div>}
    </div>
  );
}
